<template>
  <listing-layout>
    <div slot="heading" class="d-flex justify-content-between align-items-center mb-3">
      <h1 class="h2 mb-0">Pending Payments</h1>
    </div>
    <div slot="content">
      <div class="alert alert-success" v-if="success" ref="success">{{success}}</div>
      <div class="alert alert-warning" v-if="error.length" ref="error">
        <ul class="list-unstyled mb-0">
          <li v-for="(item, key) in error" :key="key">{{ item }}</li>
        </ul>
      </div>
      <data-table :dataRows="rows" :dataColumns="columns" :options="options">
        <tableTextTemplate slot-scope="{ row }" slot="expandedHeader" :text="row.user.first_name + ' ' + row.user.last_name" :row="row" :options="{ expandMobile: true, showPaid: true }" />
        <div slot-scope="{ row }" slot="expandedRow">
          <ul class="list-unstyled mb-0 pt-2">
            <li>
              <strong>Membership No: </strong>
              <tableTextTemplate :text="new Date(row.created_at.replace(/-/g, '/')).toLocaleString('en-AU')" :row="row" :options="{ inline: true }" />
            </li>
            <li>
              <strong>Amount: </strong>
              <tableTextTemplate :text="'$' + row.total" :row="row" :options="{ inline: true }" />
            </li>
            <li>
              <strong>Payment Method: </strong>
              <tableTextTemplate :text="row.payment_method.charAt(0).toUpperCase() + row.payment_method.slice(1)" :row="row" :options="{ inline: true }" />
            </li>
            <li>
              <strong>Purchase: </strong>
              <tableTextTemplate :text="row.type.charAt(0).toUpperCase() + row.type.slice(1)" :row="row" :options="{ inline: true }" />
            </li>
            <li class="pt-3 d-block">
              <button :class="['btn btn-block-mob btn-sm mb-2 mb-lg-0', processing ? 'btn-primary-dark' : 'btn-primary']" @click.prevent="approveModal(row.id)">
                {{ !processing ? 'Approve' : '' }}
                <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
              </button>
              <button :class="['btn btn-block-mob btn-sm mb-2 mb-lg-0', processing ? 'btn-primary-dark' : 'btn-primary']" @click.prevent="denyModal(row.id)">
                {{ !processing ? 'Deny' : '' }}
                <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
              </button>
              <modal v-if="showApproveModal" @close="showApproveModal = false">
                <div slot="body">
                  <div class="alert alert-warning">
                    You are about to approve the payment from member ‘{{row.user.first_name + ' ' + row.user.last_name}}’. Notify user and proceed with approval?
                  </div>
                  <div class="form-row">
                    <div class="form-group col">
                      <label for="payment_date">Payment Date</label>
                      <input v-model="payment.paymentDate" class="form-control" id="payment_date" type="date" :placeholder="[isLegacy() ? 'yyyy-mm-dd' : 'dd/mm/yyyy']" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                    </div>
                    <div class="form-group col">
                      <label for="invoice_number">Invoice Number</label>
                      <input v-model="payment.invoiceNumber" type="number" class="form-control">
                    </div>
                  </div>
                  <div class="d-flex justify-content-center">
                    <button class="btn btn-outline-primary" @click.prevent="showApproveModal = false">Cancel</button>
                    <button class="btn btn-primary ml-2" @click.prevent="approve();showApproveModal = false">Approve</button>
                  </div>
                </div>
              </modal>
              <modal v-if="showDenyModal" @close="showDenyModal = false">
                <div slot="body">
                  <div class="alert alert-warning">
                    You are about to deny the payment from member ‘{{row.user.first_name + ' ' + row.user.last_name}}’. Notify user and proceed?
                  </div>
                  <div class="d-flex justify-content-center">
                    <button class="btn btn-outline-primary" @click.prevent="showDenyModal = false">Cancel</button>
                    <button class="btn btn-primary ml-2" @click.prevent="deny();showDenyModal = false">Confirm</button>
                  </div>
                </div>
              </modal>
            </li>
          </ul>
        </div>
      </data-table>
    </div>
  </listing-layout>
</template>

<script>
import axios from 'axios'
import listingLayout from '@/components/ListingLayout'
import dataTable from '@/components/DataTable'
import tableTextTemplate from '@/components/DataTableFields/Text'
import tableButtonTemplate from '@/components/DataTableFields/Button'
import tableDownloadTemplate from '@/components/DataTableFields/Download'
import modal from '@/components/Modal'
import eventBus from '@/store/EventBus.js'
import LEGACYBROWSERS from '@/mixins/legacyBrowsers.js'

export default {
  name: 'PendingInvoices',
  mixins: [LEGACYBROWSERS],
  components: {
    'listing-layout': listingLayout,
    'data-table': dataTable,
    tableTextTemplate,
    tableButtonTemplate,
    tableDownloadTemplate,
    modal
  },
  data() {
    return {
      rows: [],
      rowID: 0,
      success: '',
      error: '',
      processing: false,
      columns: [
        {
          label: 'Membership No.',
          field: 'user.member_number',
          filterable: true,
          sortable: true,
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.user.member_number" :row="row" :options="{}" />`
          }),
          representedAs(row) { return `${row.user.member_number}` }
        },
        {
          label: 'User',
          field: 'user.first_name',
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.user.first_name + ' ' + row.user.last_name" :row="row" :options="{}" />`
          }),
          representedAs(row) { return `${row.user.first_name} ${row.user.last_name}` }
        },
        {
          label: 'Amount',
          field: 'total',
          filterable: false,
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="'$' + row.total" :row="row" :options="{ showPaid: true }" />`
          }),
          representedAs: 'total'
        },
        {
          label: 'Payment Method',
          field: 'payment_method',
          filterable: false,
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.payment_method.charAt(0).toUpperCase() + row.payment_method.slice(1)" :row="row" :options="{}" />`
          }),
          representedAs: 'payment_method'
        },
        {
          label: 'Purchase',
          field: 'type',
          filterable: false,
          headerClass: 'd-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { tableTextTemplate },
            template: `<tableTextTemplate :text="row.type.charAt(0).toUpperCase() + row.type.slice(1)" :row="row" :options="{}" />`
          }),
          representedAs: 'type'
        },
        {
          label: 'Approve',
          align: 'left',
          sortable: false,
          filterable: false,
          headerClass: 'column-lg-small d-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { modal, tableButtonTemplate },
            data() {
              return {
                showApproveModal: false,
                processing: false,
                payment: {
                  paymentDate: null,
                  invoiceNumber: null
                }
              }
            },
            methods: {
              approveModal() {
                this.showApproveModal = true
                // Resetting approve fields
                this.payment.paymentDate = null
                this.payment.invoiceNumber = null
              },
              approve() {
                this.processing = true
                this.success = ''
                eventBus.$emit('errors', [])
                axios.post(`${this.$api}/admin/update-invoice`, {
                  id: this.row.id,
                  status: 'paid',
                  invoice_number: this.payment.invoiceNumber,
                  paid_date: this.payment.paymentDate,
                  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
                }).then((response) => {
                  this.processing = false
                  if (response.data.status === 'success') {
                    this.success = 'The payment has been approved.'
                    eventBus.$emit('approve', this.payment)
                  } else if (response.data.errors) {
                    eventBus.$emit('errors', response.data.errors)
                  }
                }).catch((error) => {
                  // eslint-disable-next-line
                  console.log(error)
                  this.success = ''
                  this.error = ['Unable to receive data from the server. Please reload the page.']
                })
              }
            },
            template: `
              <div>
                <button :class="['btn btn-sm btn-block mb-2 mb-lg-0', processing ? 'btn-primary-dark' : 'btn-primary']" @click.prevent="approveModal">
                  {{ !processing ? 'Approve' : '' }}
                  <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
                </button>
                <modal v-if="showApproveModal" @close="showApproveModal = false">
                  <div slot="body">
                    <div class="alert alert-warning">
                      You are about to approve the payment from member ‘{{row.user.first_name + ' ' + row.user.last_name}}’. Notify user and proceed with approval?
                    </div>
                    <div class="form-row">
                      <div class="form-group col">
                        <label for="payment_date_desktop">Payment Date</label>
                        <input v-model="payment.paymentDate" class="form-control" id="payment_date_desktop" type="date" placeholder="dd/mm/yyyy" pattern="[0-9]{4}-[0-9]{2}-[0-9]{2}">
                      </div>
                      <div class="form-group col">
                        <label for="invoice_number">Invoice Number</label>
                        <input v-model="payment.invoiceNumber" type="number" class="form-control">
                      </div>
                    </div>
                    <div class="d-flex justify-content-center">
                      <button class="btn btn-outline-primary" @click.prevent="showApproveModal = false">Cancel</button>
                      <button class="btn btn-primary ml-2" @click.prevent="approve();showApproveModal = false">Approve</button>
                    </div>
                  </div>
                </modal>
              </div>`
          })
        },
        {
          label: 'Deny',
          align: 'left',
          sortable: false,
          filterable: false,
          headerClass: 'column-lg-small d-none d-lg-table-cell',
          visible: true,
          component: ({
            props: ['row'],
            components: { modal, tableButtonTemplate },
            data() {
              return {
                showDenyModal: false,
                processing: false
              }
            },
            methods: {
              denyModal() {
                this.showDenyModal = true
              },
              deny() {
                this.processing = true
                axios.post(`${this.$api}/admin/update-invoice`, {
                  id: this.row.id,
                  status: 'deny'
                }).then((response) => {
                  this.processing = false
                  if (response.data.status === 'success') {
                    this.success = 'The payment has been denied.'
                    eventBus.$emit('deny')
                  }
                }).catch((error) => {
                  // eslint-disable-next-line
                  console.log(error)
                  this.success = ''
                  this.error = ['Unable to receive data from the server. Please reload the page.']
                })
              }
            },
            template: `
              <div>
                <button :class="['btn btn-sm btn-block mb-2 mb-lg-0', processing ? 'btn-primary-dark' : 'btn-primary']" @click.prevent="denyModal">
                  {{ !processing ? 'Deny' : '' }}
                  <div v-if="processing" class="spinner-border text-light" role="status"><span class="sr-only">Loading...</span></div>
                </button>
                <modal v-if="showDenyModal" @close="showDenyModal = false">
                  <div slot="body">
                    <div class="alert alert-warning">
                      You are about to deny the payment from member ‘{{row.user.first_name + ' ' + row.user.last_name}}’. Do you wish to proceed?
                    </div>
                    <div class="d-flex justify-content-center">
                      <button class="btn btn-outline-primary" @click.prevent="showDenyModal = false">Cancel</button>
                      <button class="btn btn-primary ml-2" @click.prevent="deny();showDenyModal = false">Confirm</button>
                    </div>
                  </div>
                </modal>
              </div>`
          })
        }
      ],
      options: {
        search: true,
        loaded: false
      },
      showApproveModal: false,
      showDenyModal: false,
      payment: {
        paymentDate: null,
        invoiceNumber: null
      }
    }
  },
  mounted() {
    axios.get(`${this.$api}/admin/pending-invoices`).then((response) => {
      this.options.loaded = true
      if (response.data.status === 'success') {
        this.error = []
        this.rows = response.data.data
      } else {
        this.error = ['Unable to receive data from the server. Please reload the page.']
      }
    })

    eventBus.$on('approve', (payment) => {
      this.payment = payment
      this.success = 'The payment has been approved.'
      this.error = []
      this.rows = []
      this.options.loaded = false
      axios.get(`${this.$api}/admin/pending-invoices`, this.payment).then((response) => {
        this.options.loaded = true
        if (response.data.status === 'success') {
          this.rows = response.data.data
        } else {
          this.error = ['Unable to receive data from the server. Please reload the page.']
        }
      })
    })
    eventBus.$on('deny', () => {
      this.success = 'The payment has been denied.'
      this.rows = []
      this.error = []
      this.options.loaded = false
      axios.get(`${this.$api}/admin/pending-invoices`, this.payment).then((response) => {
        this.options.loaded = true
        if (response.data.status === 'success') {
          this.rows = response.data.data
        } else {
          this.error = ['Unable to receive data from the server. Please reload the page.']
        }
      })
    })
    eventBus.$on('errors', (errors) => {
      this.success = ''
      this.error = Object.values(errors).flat()
    })
  },
  methods: {
    approveModal(theRowID) {
      this.rowID = theRowID
      this.showApproveModal = true
      // Resetting approve fields
      this.payment.paymentDate = null
      this.payment.invoiceNumber = null
    },
    approve() {
      this.processing = true
      this.success = ''
      this.error = []
      axios.post(`${this.$api}/admin/update-invoice`, {
        id: this.rowID,
        status: 'paid',
        invoice_number: this.payment.invoiceNumber,
        paid_date: this.payment.paymentDate,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
      }).then((response) => {
        this.processing = false
        if (response.data.status === 'success') {
          this.success = 'The payment has been approved.'
          eventBus.$emit('approve')
        } else if (response.data.errors) {
          this.error = Object.values(response.data.errors).flat()
        }
      }).catch((error) => {
        this.processing = false
        // eslint-disable-next-line
        console.log(error)
        this.error = ['Unable to receive data from the server. Please reload the page.']
      })
    },
    denyModal(theRowID) {
      this.rowID = theRowID
      this.showDenyModal = true
    },
    deny() {
      this.processing = true
      this.success = ''
      this.error = []
      axios.post(`${this.$api}/admin/update-invoice`, {
        id: this.rowID,
        status: 'deny'
      }).then((response) => {
        this.processing = false
        if (response.data.status === 'success') {
          this.success = 'The payment has been denied.'
          eventBus.$emit('deny')
        }
      }).catch((error) => {
        this.processing = false
        // eslint-disable-next-line
        console.log(error)
        this.error = ['Unable to receive data from the server. Please reload the page.']
      })
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/views/Admin/Pending/PendingInvoices.vue